import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import "./BusinessLocationsPage.css";

const BusinessLocationsPage = () => {
  const { businessSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicNotice, setPublicNotice] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const fetchBusinessLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        setPublicNotice(null);

        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
        const response = await axios.get(
          `${apiBaseUrl}/api/business/${businessSlug}/locations-page`,
        );

        setBusinessData(response.data.business);
        setLocations(response.data.locations);
      } catch (err) {
        if (
          err.response?.data?.registrationInProgress ||
          err.response?.data?.error === "registration_in_progress"
        ) {
          setPublicNotice({
            title: err.response?.data?.title || "Shop setup in progress",
            message:
              err.response?.data?.message ||
              "Your favorite shop is still registering. Please check back in a few hours.",
            ownerMessage: err.response?.data?.ownerMessage || "",
            loginPath: err.response?.data?.loginPath || "/login",
          });
          setError(null);
        } else {
        setError(
          err.response?.data?.message ||
            "Unable to load locations. Please try again later.",
        );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessLocations();
  }, [businessSlug]);

  const handleLocationClick = (bookingUrl) => {
    navigate(bookingUrl);
  };

  const formatServiceTypes = (types) => {
    if (!types || types.length === 0) return "Full Service";
    return types.join(", ");
  };

  const formatHours = (hours) => {
    if (!hours || typeof hours !== "object") return "Hours not available";

    const daysOfWeek = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const openDays = daysOfWeek.filter(
      (day) => hours[day]?.isOpen && hours[day]?.open && hours[day]?.close,
    );

    if (openDays.length === 0) return "Hours not available";

    // Show first open day as example
    const firstDay = openDays[0];
    const dayName = firstDay.charAt(0).toUpperCase() + firstDay.slice(1);
    const { open, close } = hours[firstDay];

    return `${dayName}: ${open} - ${close}`;
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <span key={i} className="star filled">
            ★
          </span>,
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <span key={i} className="star half">
            ★
          </span>,
        );
      } else {
        stars.push(
          <span key={i} className="star empty">
            ☆
          </span>,
        );
      }
    }
    return stars;
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="business-locations-page">
          <div className="locations-loading">
            <div className="spinner"></div>
            <p>Loading locations...</p>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error) {
    return (
      <>
        <CustomerHeader />
        <div className="business-locations-page">
          <div className="locations-error">
            <h2>⚠️ {error}</h2>
            <button onClick={() => navigate("/")} className="btn-home">
              Return to Home
            </button>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const hasSession = Boolean(localStorage.getItem("ogestyle_token"));

  if (publicNotice) {
    return (
      <>
        <CustomerHeader />
        <div className="business-locations-page">
          <div
            className="locations-error"
            style={{ maxWidth: "760px", margin: "40px auto" }}
          >
            <h2 style={{ color: "#1e3a8a" }}>{publicNotice.title}</h2>
            <p style={{ color: "#334155", marginBottom: "10px" }}>
              {publicNotice.message}
            </p>
            {publicNotice.ownerMessage && (
              <p style={{ color: "#0f766e", marginBottom: "18px" }}>
                {publicNotice.ownerMessage}
              </p>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              {!hasSession && (
                <button
                  onClick={() => navigate(publicNotice.loginPath || "/login")}
                  className="btn-home"
                >
                  Log In to Finish Registration
                </button>
              )}
              <button onClick={() => navigate("/")} className="btn-home">
                Return to Home
              </button>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (!businessData) {
    return (
      <>
        <CustomerHeader />
        <div className="business-locations-page">
          <div className="locations-error">
            <h2>Business not found</h2>
            <button onClick={() => navigate("/")} className="btn-home">
              Return to Home
            </button>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{businessData.name} - All Locations | OGESTYLE</title>
        <meta
          name="description"
          content={`Find ${businessData.name} locations near you. Book beauty and wellness services at ${locations.length} ${locations.length === 1 ? "location" : "locations"}.`}
        />
        <meta
          name="keywords"
          content={`${businessData.name}, beauty services, wellness, spa, ${locations.map((l) => l.address.city).join(", ")}`}
        />
        <link
          rel="canonical"
          href={`${window.location.origin}/business/${businessData.slug}`}
        />
      </Helmet>

      <CustomerHeader />

      <div className="business-locations-page">
        {/* Hero Section with Business Branding */}
        <div className="business-hero">
          <div className="business-hero-content">
            {businessData.logo?.url && (
              <img
                src={businessData.logo.url}
                alt={businessData.name}
                className="business-logo"
              />
            )}
            <h1 className="business-name">{businessData.name}</h1>
            {businessData.description && (
              <p className="business-description">{businessData.description}</p>
            )}
            <div className="business-meta">
              {businessData.type && (
                <span className="business-type">
                  {formatServiceTypes(businessData.type)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Locations Section */}
        <div className="locations-section">
          <div className="locations-container">
            <h2 className="section-title">
              {locations.length === 1
                ? "Our Location"
                : `${locations.length} Locations to Serve You`}
            </h2>

            {locations.length === 0 ? (
              <div className="no-locations">
                <p>No locations available at this time.</p>
              </div>
            ) : (
              <div className="locations-grid">
                {locations.map((location) => (
                  <div key={location._id} className="location-card">
                    {/* Location Images Gallery */}
                    {location.images && location.images.length > 0 && (
                      <div className="location-gallery">
                        <img
                          src={location.images[0].url || location.images[0]}
                          alt={location.name}
                          className="gallery-main-image"
                        />
                        {location.images.length > 1 && (
                          <div className="gallery-indicator">
                            +{location.images.length - 1} more
                          </div>
                        )}
                      </div>
                    )}

                    <div className="location-card-header">
                      <h3 className="location-name">{location.name}</h3>
                      {location.type && location.type.length > 0 && (
                        <p className="location-service-types">
                          {formatServiceTypes(location.type)}
                        </p>
                      )}
                    </div>

                    {/* Ratings & Reviews */}
                    {location.totalReviews > 0 && (
                      <div className="location-rating">
                        <div className="stars">
                          {renderStars(location.averageRating)}
                        </div>
                        <span className="rating-text">
                          {location.averageRating.toFixed(1)} (
                          {location.totalReviews}{" "}
                          {location.totalReviews === 1 ? "review" : "reviews"})
                        </span>
                      </div>
                    )}

                    <div className="location-card-body">
                      {/* Address */}
                      <div className="location-info">
                        <div className="info-icon">📍</div>
                        <div className="info-content">
                          <strong>Address</strong>
                          <p>
                            {location.address.street}
                            <br />
                            {location.address.city}, {location.address.state}{" "}
                            {location.address.zipCode}
                          </p>
                        </div>
                      </div>

                      {/* Phone */}
                      {location.phone && (
                        <div className="location-info">
                          <div className="info-icon">📞</div>
                          <div className="info-content">
                            <strong>Phone</strong>
                            <p>
                              <a href={`tel:${location.phone}`}>
                                {location.phone}
                              </a>
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Hours */}
                      {location.hours && (
                        <div className="location-info">
                          <div className="info-icon">🕐</div>
                          <div className="info-content">
                            <strong>Hours</strong>
                            <p>{formatHours(location.hours)}</p>
                          </div>
                        </div>
                      )}

                      {/* Featured Services */}
                      {location.featuredServices &&
                        location.featuredServices.length > 0 && (
                          <div className="featured-services">
                            <strong>Popular Services</strong>
                            <ul>
                              {location.featuredServices.map((service) => (
                                <li key={service._id}>
                                  <span className="service-name">
                                    {service.name}
                                  </span>
                                  <span className="service-price">
                                    ${service.price}
                                  </span>
                                </li>
                              ))}
                            </ul>
                            {location.servicesCount > 3 && (
                              <p className="more-services">
                                +{location.servicesCount - 3} more services
                              </p>
                            )}
                          </div>
                        )}
                    </div>

                    <div className="location-card-footer">
                      <button
                        className="btn-directions"
                        onClick={() =>
                          window.open(
                            location.directionsUrl,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        📍 Get Directions
                      </button>
                      <button
                        className="btn-book-now"
                        onClick={() => handleLocationClick(location.bookingUrl)}
                      >
                        View Services & Book
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer Section with Business Links */}
        {(businessData.website || businessData.socialMedia) && (
          <div className="business-footer">
            <div className="business-footer-content">
              {businessData.website && (
                <a
                  href={businessData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  🌐 Visit Website
                </a>
              )}
              {businessData.socialMedia?.facebook && (
                <a
                  href={businessData.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  📘 Facebook
                </a>
              )}
              {businessData.socialMedia?.instagram && (
                <a
                  href={businessData.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  📷 Instagram
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      <CustomerFooter />
    </>
  );
};

export default BusinessLocationsPage;
